// CONF-227 (CONF-213 spec F9, D10 as revised 2026-10-14). "Nines" n means a survival
// probability of 1 - 10^-n. The display shows WHOLE nines only, rounded down, so it never
// claims a nine the model didn't reach: 6.6 nines reads "99.9999% (6 nines)". Under one
// nine reads "<90% (<1 nine)"; the label caps at 15+ so a near-infinite figure stays short.
const NINES_CAP = 15;

const isNinesFigure = (nines: number | null | undefined): nines is number =>
  typeof nines === 'number' && !Number.isNaN(nines) && nines > 0;

// The 1e-9 lets float noise just under a whole nine (3.9999999998) count as that nine.
const wholeNines = (nines: number): number => Math.min(NINES_CAP, Math.floor(nines + 1e-9));

export function ninesToPercent(nines: number | null | undefined): string | null {
  if (!isNinesFigure(nines)) return null;
  const k = wholeNines(nines);
  if (k < 1) return '<90';
  if (k === 1) return '90';
  if (k === 2) return '99';
  return `99.${'9'.repeat(k - 2)}`;
}

export function ninesLabel(nines: number | null | undefined): string | null {
  if (!isNinesFigure(nines)) return null;
  const k = wholeNines(nines);
  if (k < 1) return '<1';
  return k >= NINES_CAP ? `${NINES_CAP}+` : String(k);
}

export function formatNines(nines: number | null | undefined): string {
  const percent = ninesToPercent(nines);
  if (percent === null) return '—';
  const label = ninesLabel(nines);
  const unit = label === '1' || label === '<1' ? 'nine' : 'nines';
  return `${percent}% (${label} ${unit})`;
}

export function formatCapacity(value: number | undefined | null, digits = 0): string {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return '0';
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function formatMoney(value: number | undefined | null): string {
  const n = Number(value ?? 0);
  return `$${(isFinite(n) ? n : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Watts to kW for display; 0 means the metric is unavailable (classic prints N/A). */
export function formatKw(watts: number | undefined | null): string {
  const n = Number(watts ?? 0);
  return n > 0 ? (n / 1000).toFixed(1) : 'N/A';
}

/** displayValue is already display-converted by the metrics calculator (convertCapacity):
 *  TB when isPBUnit, TiB otherwise. Scale it up one tier accordingly — divide by 1000 for
 *  PB, 1024 for PiB — rather than always dividing by 1000 and mislabelling PiB mode. */
export function capacityPbParts(displayValue: number | null | undefined, isPBUnit: boolean): { figure: string; unit: 'PB' | 'PiB' } {
  const n = Number(displayValue ?? 0);
  const divisor = isPBUnit ? 1000 : 1024;
  const scaled = isFinite(n) ? n / divisor : 0;
  return { figure: scaled.toFixed(2), unit: isPBUnit ? 'PB' : 'PiB' };
}

export function formatCapacityPb(displayValue: number | null | undefined, isPBUnit: boolean): string {
  const { figure, unit } = capacityPbParts(displayValue, isPBUnit);
  return `${figure} ${unit}`;
}

/** A zero-capacity config makes $/TB figures Infinity (see deriveCostEfficiency in
 *  solutionPricing.jsx). Classic prints '$—' for that case (SpecialPricingMetrics.jsx);
 *  we deliberately don't port the '$' — the em dash stands alone. */
export function formatPerTb(value: number | null | undefined): string {
  const n = Number(value ?? 0);
  return isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

/** Guards a raw metric before .toFixed the same way formatCapacity/formatKw/formatPerTb
 *  guard theirs, so a NaN or Infinity metric (an unmet-precondition division, a missing
 *  key) renders '0' instead of leaking "NaN" / "Infinity" onto the rail. */
export function formatFixed(value: number | undefined | null, digits = 0): string {
  const n = Number(value ?? 0);
  return isFinite(n) ? n.toFixed(digits) : '0';
}
